import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiClock, FiPhone, FiMail, FiCalendar } = FiIcons;

const UrgencySection = () => {
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact-booking');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <SafeIcon icon={FiClock} className="h-8 w-8 text-white" />
            </div>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold mb-4 font-serif">
            Their Brain Won't Wait for <span className="text-accent-gold">Perfect Timing</span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-3xl mx-auto space-y-4 text-center mb-10"
        >
          <p>
            The first 5 years are a period of unprecedented brain plasticity. Waiting means missing critical windows to build foundational skills with greater ease.
          </p>
          <p>
            Your parent intuition brought you here. Trust it. <span className="font-bold text-accent-gold">The worry you feel is a signal to act.</span> Whether it's late talking, feeding struggles, behavioral challenges, or just a sense that something needs support—early intervention changes everything.
          </p>
          <p className="text-lg font-semibold">
            Stop letting well-meaning relatives convince you to "wait and see."
          </p>
          <p className="text-xl font-bold text-accent-gold">
            Your child's brightest future is built on the foundation you create today.
          </p>
          <p>
            We're so confident in early intervention's power, we guarantee meaningful progress. Support your child's development, follow our program, and if you don't see real change, we'll make it right.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center space-y-6"
        >
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={scrollToContact}
              className="bg-accent-gold text-gray-900 px-8 py-4 rounded-full font-bold hover:bg-accent-gold/90 transition-all duration-200 shadow-xl"
            >
              Book Your Child's Future Call Today (Free Consultation)
            </motion.button>
            
            <motion.a
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              href="https://www.wethebillions.com/quiz"
              className="border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white/10 transition-all duration-200"
            >
              Take the FREE Quiz
            </motion.a>
          </div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 max-w-2xl mx-auto"
          >
            <h3 className="text-xl font-bold mb-4 font-serif">Ready to Build Their Foundation?</h3>
            <div className="grid md:grid-cols-3 gap-4 text-center">
              <div className="flex flex-col items-center space-y-2">
                <SafeIcon icon={FiMail} className="h-6 w-6 text-accent-gold" />
                <p className="font-semibold text-sm">Email</p>
                <a
                  href="mailto:hello@wethebillions.com"
                  className="text-accent-gold hover:text-accent-gold/80 transition-colors text-sm"
                >
                  hello@wethebillions.com
                </a>
              </div>

              <div className="flex flex-col items-center space-y-2">
                <SafeIcon icon={FiPhone} className="h-6 w-6 text-accent-gold" />
                <p className="font-semibold text-sm">Call/Text</p>
                <a
                  href="tel:+12026560054"
                  className="text-accent-gold hover:text-accent-gold/80 transition-colors text-sm"
                >
                  (202) 656-0054
                </a>
              </div>

              <div className="flex flex-col items-center space-y-2">
                <SafeIcon icon={FiCalendar} className="h-6 w-6 text-accent-gold" />
                <p className="font-semibold text-sm">Book Online</p>
                <a
                  href="https://wethebillions.com/"
                  className="text-accent-gold hover:text-accent-gold/80 transition-colors text-sm"
                >
                  wethebillions.com
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default UrgencySection;